/**
 * サンプル管理ダッシュボードの Chart.js 共通オプション／軸ラベル。
 * 狭幅では斜めラベルより「短いラベル＋autoSkip」の方が読める。
 */

const TICK_FONT = { size: 10, weight: 'normal' as const };
const TICK_COLOR = '#6b7280';
const GRID_COLOR = 'rgba(229, 231, 235, 0.5)';

/**
 * 複数年にまたがる系列の短い軸ラベル。
 * `18/7` は日/月と誤読されやすいので `yy-MM`（ハイフン＋月ゼロ埋め）にする。
 */
export function formatRegistrationChartAxisLabel(
  month: string | null | undefined,
  spansYears: boolean,
  monthShort?: string | null
): string {
  const raw = String(month || '');
  if (spansYears && raw) {
    const [y, m] = raw.split('-');
    if (y && m) {
      const mm = Number(m);
      if (Number.isFinite(mm) && mm >= 1 && mm <= 12) {
        return `${y.slice(-2)}-${String(mm).padStart(2, '0')}`;
      }
    }
  }
  return monthShort || raw;
}

/** 表示ティック上限。多いほど間引き、少ないほど全表示 */
export function registrationChartMaxTicksLimit(labelCount: number): number {
  if (labelCount <= 4) return labelCount;
  if (labelCount <= 8) return 5;
  return 4;
}

/** 横棒のカテゴリ数に合わせてチャート高を縮め、余白だらけにしない */
export function distributionChartHeightPx(barCount: number, horizontal: boolean): number {
  if (!horizontal) return 180;
  if (barCount <= 0) return 96;
  /** カテゴリ帯 + 値軸。少なすぎるとラベルと棒がズレて見える */
  return Math.min(200, Math.max(88, 28 + barCount * 36));
}

export function buildSampleDashboardChartOptions(opts?: {
  animation?: boolean;
  indexTooltip?: boolean;
  hideTooltipColors?: boolean;
  showLegend?: boolean;
  /** true のときだけ斜め（狭幅の混雑源になりやすいので既定は横） */
  rotateXTicks?: boolean;
  maxTicksLimit?: number;
}) {
  const rotateX = opts?.rotateXTicks === true;
  return {
    responsive: true,
    maintainAspectRatio: false,
    ...(opts?.animation === false ? { animation: false as const } : {}),
    plugins: {
      legend: {
        display: opts?.showLegend === true,
        ...(opts?.showLegend ? { position: 'top' as const } : {}),
      },
      tooltip: {
        ...(opts?.indexTooltip ? { mode: 'index' as const, intersect: false } : {}),
        backgroundColor: 'rgba(31, 41, 55, 0.95)',
        padding: 12,
        titleFont: { size: 14, weight: 'bold' as const },
        bodyFont: { size: 13 },
        borderColor: '#2563eb',
        borderWidth: 2,
        cornerRadius: 8,
        ...(opts?.hideTooltipColors ? { displayColors: false } : {}),
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          maxRotation: rotateX ? 45 : 0,
          minRotation: rotateX ? 45 : 0,
          autoSkip: true,
          maxTicksLimit: opts?.maxTicksLimit ?? (rotateX ? 8 : 5),
          font: TICK_FONT,
          color: TICK_COLOR,
        },
      },
      y: {
        grid: {
          color: GRID_COLOR,
        },
        beginAtZero: true,
        ticks: {
          maxTicksLimit: 8,
          precision: 0,
          font: TICK_FONT,
          color: TICK_COLOR,
        },
      },
    },
  };
}

/**
 * 属別／BSL 棒グラフ用。
 * 横棒で数値軸設定をカテゴリ軸に載せるとラベルと棒が垂直にズレるため分離する。
 */
export function buildDistributionBarChartOptions(opts?: {
  horizontal?: boolean;
  animation?: boolean;
}) {
  const horizontal = opts?.horizontal === true;
  const base = buildSampleDashboardChartOptions({
    animation: opts?.animation,
    hideTooltipColors: true,
  });

  if (!horizontal) {
    return {
      ...base,
      scales: {
        x: {
          ...base.scales.x,
          offset: true,
        },
        y: base.scales.y,
      },
    };
  }

  return {
    ...base,
    indexAxis: 'y' as const,
    layout: {
      padding: { top: 2, bottom: 2 },
    },
    scales: {
      /** 値軸 */
      x: {
        beginAtZero: true,
        grid: { color: GRID_COLOR },
        ticks: {
          maxTicksLimit: 6,
          precision: 0,
          font: TICK_FONT,
          color: TICK_COLOR,
        },
      },
      /** カテゴリ軸: offset で帯中央に棒を置く。grid/beginAtZero は付けない */
      y: {
        offset: true,
        grid: { display: false },
        border: { display: false },
        ticks: {
          autoSkip: false,
          font: { size: 11, weight: 'normal' as const },
          color: TICK_COLOR,
          crossAlign: 'center' as const,
        },
      },
    },
  };
}
